import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { AppConfig } from "../../utils/Constants";
import { useSettingsContext } from "../../context/SettingsContext";

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsSettingsOpen, setActiveSettingsTab } = useSettingsContext();

  // Close settings when navigating to /privacy (after route change to prevent flash)
  useEffect(() => {
    if (location.pathname === "/privacy") {
      setIsSettingsOpen(false);
    }
  }, [location.pathname, setIsSettingsOpen]);

  const handleBackToSettings = () => {
    // Navigate with state to indicate we want to open settings
    navigate("/", { state: { openSettings: true, settingsTab: "profile" } });
  };

  return (
    <div className="min-h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark flex flex-col">
      {/* Simple Header */}
      <header className="border-b border-divider dark:border-divider-dark bg-background-default dark:bg-background-dark">
        <div className="container mx-auto px-6 py-8 max-w-6xl">
          <button
            onClick={handleBackToSettings}
            className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            <span className="font-medium">Back to Settings</span>
          </button>
          <h1 className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark mb-2">
            Privacy Policy
          </h1>
          <p className="text-base text-text-secondary dark:text-text-secondaryDark">
            Learn how {AppConfig.PRODUCT_NAME_SHORT} collects, uses, and protects your personal information.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8 max-w-6xl">
        <div className="space-y-16">
          {/* 1. INTRODUCTION */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">1</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Introduction
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  1.1 Our Commitment
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  {AppConfig.PRODUCT_NAME_SHORT} ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  1.2 Scope
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  This policy applies to all users of {AppConfig.PRODUCT_NAME_SHORT}, including visitors, registered users, and organizations. By using our Service, you agree to the collection and use of information in accordance with this policy.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  1.3 Changes to This Policy
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Material changes will be communicated via email or platform notification.
                </p>
              </div>
            </div>
          </section>

          {/* 2. INFORMATION WE COLLECT */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">2</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Information We Collect
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  2.1 Account Information
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  When you create an account, we collect:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Email address",
                    "Name and profile information",
                    "Password (encrypted)",
                    "Organization details",
                    "Role and permissions"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  2.2 Integration Data
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  When you connect third-party services, we access and process:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    { name: "HubSpot", desc: "Contacts, companies, deals, custom objects, and related CRM data" },
                    { name: "Stripe", desc: "Customers, subscriptions, invoices, and payment information" },
                    { name: "Asana", desc: "Projects, tasks, and team information" },
                    { name: "Slack", desc: "Workspace information and channel data" },
                    { name: "Other Services", desc: "Data as required for agent functionality" }
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">
                        <strong className="text-text-primary dark:text-text-primaryDark">{item.name}:</strong> {item.desc}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  2.3 Usage Data
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Agent execution logs and sync history",
                    "Feature usage and interaction data",
                    "Error logs and diagnostic information",
                    "IP address and browser information",
                    "Device and operating system information"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  2.4 Cookies and Tracking
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  We use cookies and similar tracking technologies to:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Maintain your session and authentication state",
                    "Remember your preferences and settings",
                    "Analyze platform usage and performance",
                    "Improve user experience"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 3. HOW WE USE YOUR INFORMATION */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">3</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                How We Use Your Information
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  3.1 Service Provision
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Provide and maintain the platform",
                    "Process agent operations and data synchronization",
                    "Manage your account and organization",
                    "Authenticate and authorize access",
                    "Send service-related communications"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  3.2 Agent Operations
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  We use your integration data to:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Execute agent workflows and automations",
                    "Sync data between connected services",
                    "Create and update records as configured",
                    "Process webhooks and events",
                    "Perform reconciliation and data validation"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  3.3 Service Improvement
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Analyze usage patterns to improve features",
                    "Debug issues and enhance reliability",
                    "Develop new features and capabilities",
                    "Conduct research and analytics (using anonymized data)"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  3.4 Communication
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Send important service updates and notifications",
                    "Respond to your inquiries and support requests",
                    "Send security alerts and account notifications",
                    "Provide customer support"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* 4. DATA SHARING AND DISCLOSURE */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">4</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Data Sharing and Disclosure
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  4.1 Third-Party Services
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  We share data with third-party services only as necessary to provide the Service:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Integration services (HubSpot, Stripe, Asana, Slack) - to execute agent operations",
                    "Cloud infrastructure providers - to host and operate the platform",
                    "Analytics services - to understand usage patterns (anonymized data)",
                    "Support tools - to provide customer support"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  4.2 Organization Members
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  Within your organization, data is accessible to members based on their role and permissions. Organization owners and admins have broader access to organization data and settings.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  4.3 Legal Requirements
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  We may disclose your information if required by law or in response to:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Valid legal process or court order",
                    "Government requests or investigations",
                    "Protection of our rights, property, or safety",
                    "Prevention of fraud or security threats"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  4.4 Business Transfers
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity. We will notify you of any such change in ownership.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  4.5 No Sale of Data
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </div>
            </div>
          </section>

          {/* 5. DATA SECURITY */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">5</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Data Security
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  5.1 Security Measures
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  We implement industry-standard security measures to protect your data:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Encryption in transit (TLS/SSL) and at rest",
                    "Secure authentication and authorization",
                    "Regular security audits and vulnerability assessments",
                    "Access controls and role-based permissions",
                    "Secure cloud infrastructure with redundancy"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  5.2 Your Responsibility
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Use strong, unique passwords",
                    "Keep your account credentials secure",
                    "Enable two-factor authentication when available",
                    "Notify us immediately of any security concerns",
                    "Review and manage integration permissions regularly"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  5.3 Data Breach Notification
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  In the event of a data breach that may affect your personal information, we will notify you and relevant authorities as required by applicable law.
                </p>
              </div>
            </div>
          </section>

          {/* 6. YOUR RIGHTS AND CHOICES */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">6</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Your Rights and Choices
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  6.1 Access and Portability
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Access your personal information through your account settings",
                    "Export your data in a machine-readable format",
                    "View agent logs and sync history",
                    "Review organization and member information"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  6.2 Correction and Deletion
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Update your account information at any time",
                    "Request correction of inaccurate data",
                    "Delete your account and associated data",
                    "Revoke integration permissions"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  6.3 Opt-Out Rights
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Unsubscribe from marketing communications",
                    "Disable non-essential cookies through browser settings",
                    "Disconnect integrations at any time",
                    "Disable specific agent operations"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  6.4 GDPR and CCPA Rights
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-4 leading-relaxed">
                  If you are located in the EU or California, you have additional rights under GDPR and CCPA:
                </p>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "Right to access, rectification, and erasure",
                    "Right to restrict processing",
                    "Right to data portability",
                    "Right to object to processing",
                    "Right to non-discrimination (CCPA)"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark mt-4 leading-relaxed">
                  To exercise these rights, contact us at {AppConfig.SUPPORT_EMAIL}.
                </p>
              </div>
            </div>
          </section>

          {/* 7. DATA RETENTION */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">7</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Data Retention
              </h2>
            </div>
            
            <div className="space-y-6 ml-14">
              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  7.1 Retention Period
                </h3>
                <ul className="space-y-2.5 ml-4">
                  {[
                    "We retain your account data while your account is active",
                    "Agent logs and sync history are retained for up to 90 days",
                    "Deleted account data is removed within 30 days of deletion request",
                    "Some data may be retained longer for legal or security purposes"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-base text-text-secondary dark:text-text-secondaryDark">
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-secondary mt-2.5"></span>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-3">
                  7.2 Backup Data
                </h3>
                <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                  Backup copies of data may be retained for disaster recovery purposes. These backups are securely stored and deleted according to our retention schedule.
                </p>
              </div>
            </div>
          </section>

          {/* 8. CHILDREN'S PRIVACY */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">8</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Children's Privacy
              </h2>
            </div>
            
            <div className="ml-14">
              <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                Our Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately at {AppConfig.SUPPORT_EMAIL}, and we will take steps to delete such information.
              </p>
            </div>
          </section>

          {/* 9. INTERNATIONAL DATA TRANSFERS */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">9</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                International Data Transfers
              </h2>
            </div>
            
            <div className="ml-14">
              <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy, including standard contractual clauses and other legal mechanisms.
              </p>
            </div>
          </section>

          {/* 10. CONTACT INFORMATION */}
          <section className="bg-background-paper dark:bg-background-paperDark rounded-2xl p-8 md:p-10 border border-divider dark:border-divider-dark shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/10 dark:bg-secondary/20 flex items-center justify-center">
                <span className="text-lg font-bold text-secondary">10</span>
              </div>
              <h2 className="text-3xl font-bold text-primary dark:text-primary-dark">
                Contact Information
              </h2>
            </div>
            
            <div className="ml-14">
              <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <p className="text-base text-text-secondary dark:text-text-secondaryDark leading-relaxed">
                Email:{" "}
                <a
                  href={`mailto:${AppConfig.SUPPORT_EMAIL}`}
                  className="text-secondary hover:text-secondary-hover font-medium transition-colors underline underline-offset-2"
                >
                  {AppConfig.SUPPORT_EMAIL}
                </a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6 text-center border-t border-divider dark:border-divider-dark bg-background-paper/50 dark:bg-background-paperDark/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <p className="text-base text-text-secondary dark:text-text-secondaryDark mb-6">
            Questions? Contact{" "}
            <a
              href={`mailto:${AppConfig.SUPPORT_EMAIL}`}
              className="text-secondary hover:text-secondary-hover font-medium transition-colors underline underline-offset-2"
            >
              {AppConfig.SUPPORT_EMAIL}
            </a>
          </p>
          <button
            onClick={handleBackToSettings}
            className="inline-flex items-center px-8 py-3 bg-secondary text-white rounded-xl font-semibold shadow-lg hover:bg-secondary-hover transition-all duration-200 hover:shadow-xl hover:scale-105 active:scale-100"
          >
            Close Privacy Policy
          </button>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;

